import { useCallback, useEffect, useRef } from "react";

import { AMBIENT_SCRIBE_SILENCE_DETECTION_SETTINGS } from "../../components/medical-scribe-silence-warning-dialog.component";

const WINDOW_MS =
  AMBIENT_SCRIBE_SILENCE_DETECTION_SETTINGS.silence_detection_sec * 1000;
const MIN_WORDS = 3;

interface UseFrontendSilenceDetectionProps {
  isRecording: boolean;
  onSilenceDetected: () => void;
  isEnabled: boolean;
}

/**
 * FE-side silence detection based on transcription word count.
 *
 * Maintains a rolling window of `silence_detection_sec` seconds. If fewer than
 * MIN_WORDS (3) final-transcript words are received before the window expires,
 * `onSilenceDetected` is called. Receiving 3+ words resets the window.
 */
export function useFrontendSilenceDetection({
  isRecording,
  onSilenceDetected,
  isEnabled
}: UseFrontendSilenceDetectionProps) {
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wordCountRef = useRef(0);

  // Refs for values read inside setTimeout callbacks to avoid stale closures
  const onSilenceDetectedRef = useRef(onSilenceDetected);
  const isEnabledRef = useRef(isEnabled);

  useEffect(() => {
    onSilenceDetectedRef.current = onSilenceDetected;
  }, [onSilenceDetected]);

  useEffect(() => {
    isEnabledRef.current = isEnabled;
  }, [isEnabled]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const startSilenceTimer = useCallback(() => {
    if (!isEnabledRef.current) return;
    clearSilenceTimer();
    wordCountRef.current = 0;
    silenceTimerRef.current = setTimeout(() => {
      onSilenceDetectedRef.current();
    }, WINDOW_MS);
  }, [clearSilenceTimer]);

  // Start timer when recording begins; clear when recording stops or feature is disabled
  useEffect(() => {
    if (isRecording && isEnabled) {
      startSilenceTimer();
    } else {
      clearSilenceTimer();
    }
    return () => clearSilenceTimer();
  }, [isRecording, isEnabled, startSilenceTimer, clearSilenceTimer]);

  // Called for each transcription chunk received from BE (final transcripts only)
  const onTranscription = useCallback(
    (text: string, isFinal: boolean) => {
      if (!isFinal) return;
      const words = text.trim().split(/\s+/).filter(Boolean);
      wordCountRef.current += words.length;
      if (wordCountRef.current >= MIN_WORDS) {
        startSilenceTimer();
      }
    },
    [startSilenceTimer]
  );

  return { onTranscription, restartTimer: startSilenceTimer };
}
